import { Alert, Form, InputNumber, Select, Switch } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  MAX_DURATION_DAYS,
  cadencePresetOptions,
  delayPresetOptions
} from "../../helpers/duration-presets"
import { journeySteps, scheduleWarningText } from "../../helpers/form-summary"
import {
  type AutomationFormValues,
  type ScheduleWarning,
  cadenceTotalHours,
  delayTotalMinutes,
  formValuesToPayload
} from "../../helpers/form-values"
import { localizeDuration } from "../../helpers/localized-summary"
import type { AutomationOptions } from "../../types/automation.types"
import { BuilderSection } from "./BuilderSection"
import { DurationControl } from "./DurationControl"

// The live review's journey on one line, in its words: the same steps, the
// wait without its "after first entry" (the helper above says that).
function FlowLine({
  values,
  options
}: {
  values: AutomationFormValues
  options: AutomationOptions
}) {
  const { t } = useTranslation()
  const state =
    options.lead_states.find((item) => item.id === values.lead_status)?.label ??
    t("settingsAutomation.summary.aState")
  const payload = formValuesToPayload(values)
  const [trigger, , send] = journeySteps(t, values, options)
  const steps = [
    values.category === "NURTURING"
      ? trigger.text
      : t("settingsAutomation.builder.trigger.flowEnters", { state }),
    t("settingsAutomation.builder.trigger.flowWait", {
      duration: localizeDuration(
        t,
        payload.initial_delay_value,
        payload.initial_delay_unit
      )
    }),
    payload.send_count === 1
      ? send.title
      : t("settingsAutomation.builder.trigger.flowSend", {
          title: send.title,
          text: send.text
        })
  ]
  return (
    <div className={"mb-6 rounded-lg bg-gray-50 px-3 py-2"}>
      <p className={"m-0 mb-1 text-xs font-medium text-gray-600"}>
        {t("settingsAutomation.builder.trigger.flowTitle")}
      </p>
      <p
        data-testid={"automation-flow"}
        className={"m-0 flex flex-wrap items-center gap-x-2 text-sm"}
      >
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <span aria-hidden={"true"} className={"text-gray-500"}>
                →
              </span>
            )}
            <span>{step}</span>
          </React.Fragment>
        ))}
      </p>
    </div>
  )
}

export function TriggerTimingSection({
  options,
  values,
  complete,
  scheduleWarning = null
}: {
  options: AutomationOptions
  values: AutomationFormValues
  complete: boolean
  /** What saving would do to this automation's Nurturing sends. */
  scheduleWarning?: ScheduleWarning | null
}) {
  const { t } = useTranslation()
  const maxSends = options.limits.max_sends
  const nurturing = values.category === "NURTURING"
  return (
    <BuilderSection sectionKey={"trigger"} complete={complete}>
      {scheduleWarning && (
        <Alert
          type={"warning"}
          showIcon
          className={"mb-4"}
          data-testid={"automation-schedule-restart"}
          message={scheduleWarningText(t, scheduleWarning, values)}
        />
      )}

      {/* Unmounted, not cleared: the store keeps the pick for a switch back to Lead. */}
      {!nurturing && (
        <Form.Item<AutomationFormValues>
          label={t("settingsAutomation.builder.trigger.stateLabel")}
          name={"lead_status"}
          extra={t("settingsAutomation.builder.trigger.firstEntryHelp")}
          rules={[
            {
              required: true,
              message: t("settingsAutomation.builder.trigger.stateRequired")
            }
          ]}
        >
          <Select
            placeholder={t(
              "settingsAutomation.builder.trigger.statePlaceholder"
            )}
            showSearch
            optionFilterProp={"label"}
            options={options.lead_states.map((state) => ({
              value: state.id,
              label: state.label
            }))}
          />
        </Form.Item>
      )}

      <DurationControl
        id={"automation-first-delay"}
        label={t("settingsAutomation.builder.trigger.delayLabel")}
        extra={t(
          `settingsAutomation.builder.trigger.${nurturing ? "nurturingDelayHelp" : "delayHelp"}`
        )}
        presetName={"delay_preset"}
        presets={delayPresetOptions(t)}
        parts={[
          {
            name: "delay_weeks",
            label: t("settingsAutomation.builder.trigger.delayWeeks"),
            unit: t("settingsAutomation.duration.units.weeks")
          },
          {
            name: "delay_days",
            label: t("settingsAutomation.builder.trigger.delayDays"),
            unit: t("settingsAutomation.duration.units.days")
          },
          {
            name: "delay_hours",
            label: t("settingsAutomation.builder.trigger.delayHours"),
            unit: t("settingsAutomation.duration.units.hours")
          },
          {
            name: "delay_minutes",
            label: t("settingsAutomation.builder.trigger.delayMinutes"),
            unit: t("settingsAutomation.duration.units.minutes")
          }
        ]}
        totalRule={[
          ({ getFieldValue }) => ({
            validator: () => {
              const total = delayTotalMinutes({
                delay_weeks: getFieldValue("delay_weeks"),
                delay_days: getFieldValue("delay_days"),
                delay_hours: getFieldValue("delay_hours"),
                delay_minutes: getFieldValue("delay_minutes")
              })
              if (total < 1) {
                return Promise.reject(
                  new Error(t("settingsAutomation.builder.trigger.delayMin"))
                )
              }
              if (total > MAX_DURATION_DAYS * 24 * 60) {
                return Promise.reject(
                  new Error(
                    t("settingsAutomation.builder.trigger.delayMax", {
                      days: MAX_DURATION_DAYS
                    })
                  )
                )
              }
              return Promise.resolve()
            }
          })
        ]}
      />

      <FlowLine values={values} options={options} />

      <Form.Item<AutomationFormValues>
        label={t("settingsAutomation.builder.trigger.repeatLabel")}
        name={"repeat"}
        valuePropName={"checked"}
        extra={
          values.repeat
            ? undefined
            : t("settingsAutomation.builder.trigger.repeatOff")
        }
      >
        <Switch />
      </Form.Item>

      {values.repeat && (
        <div className={"grid grid-cols-1 gap-x-4 sm:grid-cols-2"}>
          <Form.Item<AutomationFormValues>
            label={t("settingsAutomation.builder.trigger.totalLabel")}
            name={"send_count"}
            extra={t("settingsAutomation.builder.trigger.totalHelp", {
              max: maxSends
            })}
            rules={[
              {
                required: true,
                message: t("settingsAutomation.builder.trigger.totalRequired")
              },
              {
                type: "number",
                min: 2,
                max: maxSends,
                // Plural only: the range always starts at 2.
                message: t("settingsAutomation.builder.trigger.totalRange", {
                  max: maxSends
                })
              }
            ]}
          >
            <InputNumber
              min={1}
              max={maxSends}
              precision={0}
              className={"w-28"}
            />
          </Form.Item>
          <DurationControl
            id={"automation-repeat-every"}
            label={t("settingsAutomation.builder.trigger.cadenceLabel")}
            presetName={"cadence_preset"}
            presets={cadencePresetOptions(t)}
            parts={[
              {
                name: "cadence_weeks",
                label: t("settingsAutomation.builder.trigger.cadenceWeeks"),
                unit: t("settingsAutomation.duration.units.weeks")
              },
              {
                name: "cadence_days",
                label: t("settingsAutomation.builder.trigger.cadenceDays"),
                unit: t("settingsAutomation.duration.units.days")
              },
              {
                name: "cadence_hours",
                label: t("settingsAutomation.builder.trigger.cadenceHours"),
                unit: t("settingsAutomation.duration.units.hours")
              }
            ]}
            totalRule={[
              ({ getFieldValue }) => ({
                validator: () => {
                  const total = cadenceTotalHours({
                    cadence_weeks: getFieldValue("cadence_weeks"),
                    cadence_days: getFieldValue("cadence_days"),
                    cadence_hours: getFieldValue("cadence_hours")
                  })
                  if (total < 1) {
                    return Promise.reject(
                      new Error(
                        t("settingsAutomation.builder.trigger.cadenceMin")
                      )
                    )
                  }
                  if (total > MAX_DURATION_DAYS * 24) {
                    return Promise.reject(
                      new Error(
                        t("settingsAutomation.builder.trigger.cadenceMax", {
                          days: MAX_DURATION_DAYS
                        })
                      )
                    )
                  }
                  return Promise.resolve()
                }
              })
            ]}
          />
        </div>
      )}
    </BuilderSection>
  )
}
